/**
 * Elite page below-the-fold showcase (Asana 1219949663037427). Every tunable
 * number the four sections and the sticky bar use lives here. No fee number
 * does: those come from MONTHLY_MAINTENANCE_FEE_POINTS and useTransactionFeeRate.
 */

export type StudioFeatureKey = 'border' | 'chat' | 'badge' | 'url' | 'social' | 'trophy';

export const STUDIO_FEATURE_ORDER: StudioFeatureKey[] = ['border', 'chat', 'badge', 'url', 'social', 'trophy'];

/** Compare-card anchor ids (ElitePage feature rows) mapped to the studio feature they select. */
export const STUDIO_ANCHOR_TO_FEATURE: Record<string, StudioFeatureKey> = {
  'feature-animated-border': 'border',
  'feature-chat-customization': 'chat',
  'feature-custom-url': 'url',
  'feature-social-links': 'social',
  'feature-elite-badge': 'trophy',
};

export const PROFILE_STUDIO_ID = 'elite-profile-studio';
export const SMS_SHOWCASE_ID = 'elite-sms-alerts';

/** Auto-cycle the selected studio feature until the first interaction. */
export const FEATURE_CYCLE_MS = 3600;

export const BORDER_PRESETS: ReadonlyArray<readonly [string, string]> = [
  ['#FFD700', '#9B59B6'],
  ['#22d3ee', '#a855f7'],
  ['#f97316', '#ef4444'],
  ['#34d399', '#3b82f6'],
];

export const BUBBLE_PRESETS: ReadonlyArray<readonly [string, string]> = [
  ['#8B5CF6', '#6D28D9'],
  ['#f59e0b', '#ea580c'],
  ['#ec4899', '#8b5cf6'],
  ['#10b981', '#0ea5e9'],
];

export const SLUG_MAX_LENGTH = 20;
export const SLUG_ALLOWED_RE = /[^a-z0-9_-]/g;

export const FEE_CALC_MIN = 0;
export const FEE_CALC_MAX = 250000;
export const FEE_CALC_FALLBACK_VOLUME = 2000;
/** The typed value's ceiling (Asana 1219988885932257, Decision 3). */
export const FEE_CALC_INPUT_CAP = 99999999;

/**
 * Progressive slider stops (Asana 1219988885932257, Decision 3): finer near
 * zero, coarser as the volume grows. The range input is index-based over them.
 */
const feeStopRanges: ReadonlyArray<readonly [from: number, to: number, step: number]> = [
  [FEE_CALC_MIN, 5000, 250],
  [5000, 20000, 1000],
  [20000, 100000, 5000],
  [100000, FEE_CALC_MAX, 25000],
];

const buildFeeStops = (): number[] => {
  const stops: number[] = [];
  feeStopRanges.forEach(([from, to, step]) => {
    for (let v = from; v <= to; v += step) {
      if (stops[stops.length - 1] !== v) stops.push(v);
    }
  });
  return stops;
};

export const FEE_CALC_STOPS: readonly number[] = buildFeeStops();

export const TOAST_ROTATE_MS = 2800;
export const SMS_SHOWCASE_SCRYFALL_ID = '273f51d3-17ee-42f4-a924-72ade5ae9896';
export const BELL_ICON_URL =
  'https://trademagic-assets.s3.us-west-1.amazonaws.com/assets/icons/notifications_active_24dp.svg';

/** Breakpoint shared with elite.css for the mobile layout. */
export const ELITE_MOBILE_MAX_WIDTH = 768;

/** Gap kept between the sticky bar and any in-page content under it. */
export const STICKY_BAR_CONTENT_GAP = 20;
export const STICKY_BAR_BOTTOM_OFFSET_DESKTOP = 20;
export const STICKY_BAR_BOTTOM_OFFSET_MOBILE = 16;

export const CHECKOUT_SOURCES = {
  studio: 'elite-page-studio',
  fees: 'elite-page-fees',
  sticky: 'elite-page-sticky',
  bottom: 'elite-page-bottom',
} as const;
